/* eslint-disable no-use-before-define */
/* eslint-disable no-await-in-loop */
/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable fp/no-mutation */
/* eslint-disable fp/no-mutating-methods */
import {
  createAsyncThunk,
  createSlice as _createSlice,
  PayloadAction,
} from '@reduxjs/toolkit'
import dayjs from 'dayjs'
import pLimit from 'p-limit'
import { AppRootState, Status } from 'src/store/store'
import axios, { pluckData } from 'src/utilities/http-client'
import { UserExtra as User, UserInvite } from 'src/user/user.types'
import {
  Assignment,
  Class,
  Course,
  Credential,
  ExternalCertificate,
  License,
  LinkedService,
  LiveCertificate,
  RecordedCertificate,
  Section,
  TrainingSeries,
} from 'src/v3/core/types'

export type PaginatedResponse<T> = {
  data: T[]
  pagination: {
    page: number
    pageSize: number
    totalCount: number
    totalPages: number
    hasNextPage: boolean
    hasPreviousPage: boolean
  }
}

let fetchAllPages = async <T>(
  endpoint: string,
  pageSize: number = 500,
  concurrency: number = 5,
): Promise<T[]> => {
  let limit = pLimit(concurrency)

  let firstResponse = await axios.get<PaginatedResponse<T>>(endpoint, {
    params: { page: 1, pageSize },
  })

  let allData = [...firstResponse.data.data]
  let { totalPages } = firstResponse.data.pagination

  if (totalPages > 1) {
    let remainingPages = Array.from({ length: totalPages - 1 }, (_, i) => i + 2)

    let remainingRequests = remainingPages.map((page) =>
      limit(() =>
        axios.get<PaginatedResponse<T>>(endpoint, {
          params: { page, pageSize },
        }),
      ),
    )

    let remainingResponses = await Promise.all(remainingRequests)
    let remainingData = remainingResponses.flatMap((res) => res.data.data)
    allData = [...allData, ...remainingData]
  }

  return allData
}

export type CompletedAssignment = {
  assignmentID: string
  dateCompleted: string
  linkedServiceID: string
  userID: string
}

export type UserGroup = {
  id?: string
  name: string
  linkedServiceID: string
  users?: string[]
  createdTime?: string
  updatedTime?: string
}

export type UserGroupFormValues = {
  id?: string
  name: string
  linkedServiceID: string
}

export type UserHours = {
  courseCode: string
  courseLength: number
  courseName: string
  firstName: string
  lastName: string
  createdTime: number
}

export type TimeUnit = 'week' | 'month' | 'year' | 'allTime'

export type UserHoursV3 = {
  linkedServiceID: string
} & Record<
  TimeUnit,
  {
    userID: string
    hours: number
  }[]
>

export type CompletedCourse = {
  assignmentID: string
  courseID: string
  dateCompleted: string
  userID: string
}

type QuizByPassFail = {
  className: string
  courseCode: string
  quizId: string
  totalAttempts: number
  passedCount: number
  failedCount: number
  passedRate: number
  failedRate: number
}

export type QuestionByPassFail = {
  className: string
  quizName: string
  questionText: string
  courseCode: string
  quizId: string
  totalAttempts: number
  passedCount: number
  failedCount: number
  passedRate: number
  failedRate: number
}

export type QuestionBreakdown = {
  quizID: string
  questionID: string
  questionText: string
  choiceText: string
  numberTimesPicked: number
  percentChosen: number
  isCorrect: string
}

export type QuizData = {
  quizRate: QuizByPassFail[]
  questionRate: QuestionByPassFail[]
  questionBreakdown: QuestionBreakdown[]
}

export let fetchSections = createAsyncThunk('manage/fetchSectionsStatus', () =>
  fetchAllPages<Section>('/api/v2/manage/sections'),
)

export let fetchUserHours = createAsyncThunk(
  'manage/fetchUserHoursStatus',
  () =>
    axios
      .get<UserHoursV3[]>('/api/v2/manage/dashboard/user-hours')
      .then(pluckData),
)

export let fetchQuizAnalytics = createAsyncThunk(
  'manage/fetchQuizAnalyticsStatus',
  (linkedServiceID: string) =>
    axios
      .get<QuizData>(
        `/api/quiz-events-by-linked-service?linkedServiceId=${linkedServiceID}`,
      )
      .then(pluckData),
)

export let fetchGroups = createAsyncThunk('manage/fetchGroupsStatus', () =>
  fetchAllPages<UserGroup>('/api/v2/manage/groups'),
)

export let fetchUsers = createAsyncThunk('manage/fetchUsersStatus', () =>
  fetchAllPages<User>('/api/v2/manage/users'),
)

export let fetchAssignments = createAsyncThunk(
  'manage/fetchAssignmentsStatus',
  () => fetchAllPages<Assignment>('/api/v2/manage/assignments'),
)

export let fetchCompletedAssignmentsWithinThreshold = createAsyncThunk(
  'manage/fetchCompletedAssignmentsWithinThresholdStatus',
  (id: string) =>
    axios
      .get<
        CompletedCourse[]
      >(`/api/manage/assignment-completed-within-threshold?assignmentID=${id}`)
      .then(pluckData),
)

export let fetchCompletedAssignments = createAsyncThunk(
  'manage/fetchCompletedAssignmentsStatus',
  () =>
    axios
      .get<CompletedAssignment[]>('/api/v3/manage/completed-assignments')
      .then(pluckData),
)

export let fetchCompletedCourses = createAsyncThunk(
  'manage/fetchCompletedCoursesStatus',
  () =>
    fetchAllPages<CompletedCourse>('/api/manage/assignment-courses/completed'),
)

export let fetchTrainingSeries = createAsyncThunk(
  'manage/fetchTrainingSeriesStatus',
  () => fetchAllPages<TrainingSeries>('/api/v2/manage/training-series'),
)

export let fetchClasses = createAsyncThunk('manage/fetchClassesStatus', () =>
  fetchAllPages<Class>('/api/v2/manage/classes'),
)

export let fetchCourses = createAsyncThunk('manage/fetchCoursesStatus', () =>
  fetchAllPages<Course>('/api/v2/manage/hydrated-courses'),
)

export let fetchInvites = createAsyncThunk(
  'manage/fetchInvitesStatus',
  (id: string) =>
    axios
      .get<
        UserInvite[]
      >(`/api/v2/manage/linked-service/invites/by-linked-service?linkedServiceID=${id}`)
      .then(pluckData),
)

export let fetchCertificates = createAsyncThunk(
  'manage/fetchCertificatesStatus',
  () => fetchAllPages<RecordedCertificate>('/api/v2/manage/certificates'),
)

export let fetchLiveCertificates = createAsyncThunk(
  'manage/fetchLiveCertificatesStatus',
  () =>
    fetchAllPages<LiveCertificate>('/api/v2/manage/live-class-certificates'),
)

export let fetchExternalCertificates = createAsyncThunk(
  'manage/fetchExternalCertificatesStatus',
  () =>
    fetchAllPages<ExternalCertificate>('/api/v2/manage/external-certificates'),
)

export let fetchCredentials = createAsyncThunk(
  'manage/fetchCredentialsStatus',
  () => fetchAllPages<Credential>('/api/v2/manage/credentials'),
)

export let fetchLinkedServices = createAsyncThunk(
  'linkedService/fetchLinkedServices',
  () => fetchAllPages<LinkedService>('/api/v2/manage/linked-services'),
)

export let fetchLicenses = createAsyncThunk(
  'manage/fetchLicenses',
  (linkedServiceID: string) =>
    axios
      .get<License[]>(`/api/manage/licenses?linkedServiceID=${linkedServiceID}`)
      .then(pluckData),
)

export let exportCredentials = createAsyncThunk(
  'manage/exportCredentials',
  async (linkedServiceID: string) => {
    let fetchPage = async (
      page: number,
      accumulated: string[] = [],
    ): Promise<string[]> => {
      let response = await axios.get('/manage/credentials/export', {
        params: { linkedServiceID, page, pageSize: 2000 },
        responseType: 'text',
      })

      let newAccumulated = [...accumulated, response.data]
      let hasNextPage = response.headers['x-pagination-hasnext'] === 'true'

      return hasNextPage ? fetchPage(page + 1, newAccumulated) : newAccumulated
    }

    let csvChunks = await fetchPage(1)
    let finalCSV = csvChunks.join('\n')
    let blob = new Blob([finalCSV], { type: 'text/csv' })
    let url = URL.createObjectURL(blob)
    let a = document.createElement('a')
    a.href = url
    a.download = 'credentials-export.csv'
    a.click()
    URL.revokeObjectURL(url)

    return { success: true }
  },
)

export let exportCertificates = createAsyncThunk(
  'manage/exportCertificates',
  async (linkedServiceID: string) => {
    let csvChunks = await fetchAllPages<string[]>(
      `/api/v2/manage/certificates/export?linkedServiceID=${linkedServiceID}`,
    )

    let finalCSV = csvChunks.join('\n')
    let blob = new Blob([finalCSV], { type: 'text/csv' })
    let url = URL.createObjectURL(blob)
    let a = document.createElement('a')
    a.href = url
    a.download = 'certificates-export.csv'
    a.click()
    URL.revokeObjectURL(url)

    return { success: true }
  },
)

export let exportUserTranscripts = createAsyncThunk(
  'manage/exportUserTranscripts',
  async (params: { userIDs: string[]; startDate: any; endDate: any }) => {
    let fetchPage = async (
      page: number,
      accumulated: string[] = [],
    ): Promise<string[]> => {
      let response = await axios.post(
        '/api/manage/user/transcripts',
        {
          ...params,
          page,
          pageSize: 2000,
        },
        { responseType: 'text' },
      )

      let newAccumulated = [...accumulated, response.data]
      let hasNextPage = response.headers['x-pagination-hasnext'] === 'true'

      return hasNextPage ? fetchPage(page + 1, newAccumulated) : newAccumulated
    }

    let csvChunks = await fetchPage(1)
    let finalCSV = csvChunks.join('\n')
    let blob = new Blob([finalCSV], { type: 'text/csv' })
    let url = URL.createObjectURL(blob)
    let a = document.createElement('a')
    a.href = url
    a.download = 'user-transcripts-export.csv'
    a.click()
    URL.revokeObjectURL(url)

    return { success: true }
  },
)

export let exportLinkedServiceRoster = createAsyncThunk(
  'manage/exportLinkedServiceRoster',
  async (linkedServiceID: string) => {
    let fetchPage = async (
      page: number,
      accumulated: string[] = [],
    ): Promise<string[]> => {
      let response = await axios.get(
        '/api/v2/manage/linked-service/roster/export',
        {
          params: { linkedServiceID, page, pageSize: 2000 },
          responseType: 'text',
        },
      )

      let newAccumulated = [...accumulated, response.data]
      let hasNextPage = response.headers['x-pagination-hasnext'] === 'true'

      return hasNextPage ? fetchPage(page + 1, newAccumulated) : newAccumulated
    }

    let csvChunks = await fetchPage(1)
    let finalCSV = csvChunks.join('\n')
    let blob = new Blob([finalCSV], { type: 'text/csv' })
    let url = URL.createObjectURL(blob)
    let a = document.createElement('a')
    a.href = url
    a.download = 'roster-export.csv'
    a.click()
    URL.revokeObjectURL(url)

    return { success: true }
  },
)

type EntityStatus = string | 'unselected'

type ManageState = {
  activeLinkedService: EntityStatus
  userHoursStatus: Status
  userHours: UserHoursV3[]
  groupsStatus: Status
  groups: UserGroup[]
  usersStatus: Status
  users: User[]
  assignmentsStatus: Status
  assignments: Assignment[]
  completedAssignmentsWithinThresholdStatus: Record<string, Status>
  completedAssignmentsWithinThreshold: Record<string, CompletedCourse[]>
  completedAssignmentsStatus: Status
  completedAssignments: CompletedAssignment[]
  completedCoursesStatus: Status
  completedCourses: CompletedCourse[]
  trainingSeries: TrainingSeries[]
  trainingSeriesStatus: Status
  sections: Section[]
  sectinsStatus: Status
  classes: Class[]
  classesStatus: Status
  courses: Course[]
  coursesStatus: Status
  invites: UserInvite[]
  invitesStatus: Status
  certificates: RecordedCertificate[]
  certificatesStatus: Status
  liveCertificates: LiveCertificate[]
  liveCertificatesStatus: Status
  externalCertificates: ExternalCertificate[]
  externalCertificatesStatus: Status
  credentials: Credential[]
  credentialsStatus: Status
  quizAnalytics: Record<string, QuizData>
  quizAnalyticsStatus: Status
  linkedServicesStatus: Status
  linkedServices: LinkedService[]
  successMessage: string
  errorMessage: string
  licensesStatus: Record<string, Status>
  licenses: Record<string, License[]>
}

export let initialState: ManageState = {
  activeLinkedService: 'unselected',
  userHoursStatus: 'uninitialized',
  userHours: [],
  groupsStatus: 'uninitialized',
  groups: [],
  usersStatus: 'uninitialized',
  users: [],
  assignmentsStatus: 'uninitialized',
  assignments: [],
  completedAssignmentsStatus: 'uninitialized',
  completedAssignments: [],
  completedAssignmentsWithinThresholdStatus: {},
  completedAssignmentsWithinThreshold: {},
  completedCoursesStatus: 'uninitialized',
  completedCourses: [],
  trainingSeriesStatus: 'uninitialized',
  trainingSeries: [],
  sectinsStatus: 'uninitialized',
  sections: [],
  coursesStatus: 'uninitialized',
  courses: [],
  classesStatus: 'uninitialized',
  classes: [],
  invitesStatus: 'uninitialized',
  invites: [],
  certificatesStatus: 'uninitialized',
  certificates: [],
  liveCertificatesStatus: 'uninitialized',
  liveCertificates: [],
  externalCertificatesStatus: 'uninitialized',
  externalCertificates: [],
  credentialsStatus: 'uninitialized',
  credentials: [],
  quizAnalyticsStatus: 'uninitialized',
  quizAnalytics: {},
  linkedServicesStatus: 'uninitialized',
  linkedServices: [],
  successMessage: '',
  errorMessage: '',
  licensesStatus: {},
  licenses: {},
}

export let manageSlice = _createSlice({
  name: 'manage',
  initialState,
  reducers: {
    activeLinkedServiceChanged: (state, action: PayloadAction<string>) => {
      state.activeLinkedService = action.payload
    },
    groupsChanged: (state, action: PayloadAction<UserGroup[]>) => {
      state.groups = action.payload
    },
    usersChanged: (state, action: PayloadAction<User[]>) => {
      state.users = action.payload
    },
    assignmentsChanged: (state, action: PayloadAction<Assignment[]>) => {
      state.assignments = action.payload
    },
    trainingSeriesChanged: (state, action: PayloadAction<TrainingSeries[]>) => {
      state.trainingSeries = action.payload
    },
    invitesChanged: (state, action: PayloadAction<UserInvite[]>) => {
      state.invites = action.payload
    },
    certificatesChanged: (
      state,
      action: PayloadAction<RecordedCertificate[]>,
    ) => {
      state.certificates = action.payload
    },
    credentialsChanged: (state, action: PayloadAction<Credential[]>) => {
      state.credentials = action.payload
    },
    successMessageChanged: (state, action: PayloadAction<string>) => {
      state.successMessage = action.payload
    },
    errorMessageChanged: (state, action: PayloadAction<string>) => {
      state.errorMessage = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserHours.pending, (state) => {
        state.userHoursStatus = 'pending'
      })
      .addCase(fetchUserHours.rejected, (state) => {
        state.userHoursStatus = 'rejected'
      })
      .addCase(
        fetchUserHours.fulfilled,
        (state, action: PayloadAction<UserHoursV3[]>) => {
          state.userHoursStatus = 'fulfilled'
          state.userHours = action.payload
        },
      )
      .addCase(fetchGroups.pending, (state) => {
        state.groupsStatus = 'pending'
      })
      .addCase(fetchGroups.rejected, (state) => {
        state.groupsStatus = 'rejected'
      })
      .addCase(
        fetchGroups.fulfilled,
        (state, action: PayloadAction<UserGroup[]>) => {
          state.groupsStatus = 'fulfilled'
          state.groups = action.payload
        },
      )
      .addCase(fetchUsers.pending, (state) => {
        state.usersStatus = 'pending'
      })
      .addCase(fetchUsers.rejected, (state) => {
        state.usersStatus = 'rejected'
      })
      .addCase(fetchUsers.fulfilled, (state, action: PayloadAction<User[]>) => {
        state.usersStatus = 'fulfilled'
        state.users = action.payload
      })
      .addCase(fetchAssignments.pending, (state) => {
        state.assignmentsStatus = 'pending'
      })
      .addCase(fetchAssignments.rejected, (state) => {
        state.assignmentsStatus = 'rejected'
      })
      .addCase(
        fetchAssignments.fulfilled,
        (state, action: PayloadAction<Assignment[]>) => {
          state.assignmentsStatus = 'fulfilled'
          state.assignments = action.payload
        },
      )
      .addCase(
        fetchCompletedAssignmentsWithinThreshold.pending,
        (state, action) => {
          state.completedAssignmentsWithinThresholdStatus[action.meta.arg] =
            'pending'
        },
      )
      .addCase(
        fetchCompletedAssignmentsWithinThreshold.rejected,
        (state, action) => {
          state.completedAssignmentsWithinThresholdStatus[action.meta.arg] =
            'rejected'
        },
      )
      .addCase(
        fetchCompletedAssignmentsWithinThreshold.fulfilled,
        (state, action) => {
          state.completedAssignmentsWithinThresholdStatus[action.meta.arg] =
            'fulfilled'
          state.completedAssignmentsWithinThreshold[action.meta.arg] =
            action.payload
        },
      )
      .addCase(fetchCompletedAssignments.pending, (state) => {
        state.completedAssignmentsStatus = 'pending'
      })
      .addCase(fetchCompletedAssignments.rejected, (state) => {
        state.completedAssignmentsStatus = 'rejected'
      })
      .addCase(
        fetchCompletedAssignments.fulfilled,
        (state, action: PayloadAction<CompletedAssignment[]>) => {
          state.completedAssignmentsStatus = 'fulfilled'
          state.completedAssignments = action.payload
        },
      )
      .addCase(fetchCompletedCourses.pending, (state) => {
        state.completedCoursesStatus = 'pending'
      })
      .addCase(fetchCompletedCourses.rejected, (state) => {
        state.completedCoursesStatus = 'rejected'
      })
      .addCase(
        fetchCompletedCourses.fulfilled,
        (state, action: PayloadAction<CompletedCourse[]>) => {
          state.completedCoursesStatus = 'fulfilled'
          state.completedCourses = action.payload
        },
      )
      .addCase(fetchTrainingSeries.pending, (state) => {
        state.trainingSeriesStatus = 'pending'
      })
      .addCase(fetchTrainingSeries.rejected, (state) => {
        state.trainingSeriesStatus = 'rejected'
      })
      .addCase(
        fetchTrainingSeries.fulfilled,
        (state, action: PayloadAction<TrainingSeries[]>) => {
          state.trainingSeriesStatus = 'fulfilled'
          state.trainingSeries = action.payload
        },
      )
      .addCase(fetchSections.pending, (state) => {
        state.sectinsStatus = 'pending'
      })
      .addCase(fetchSections.rejected, (state) => {
        state.sectinsStatus = 'rejected'
      })
      .addCase(
        fetchSections.fulfilled,
        (state, action: PayloadAction<Section[]>) => {
          state.sectinsStatus = 'fulfilled'
          state.sections = action.payload
        },
      )
      .addCase(fetchClasses.pending, (state) => {
        state.classesStatus = 'pending'
      })
      .addCase(fetchClasses.rejected, (state) => {
        state.classesStatus = 'rejected'
      })
      .addCase(
        fetchClasses.fulfilled,
        (state, action: PayloadAction<Class[]>) => {
          state.classesStatus = 'fulfilled'
          state.classes = action.payload
        },
      )
      .addCase(fetchCourses.pending, (state) => {
        state.coursesStatus = 'pending'
      })
      .addCase(fetchCourses.rejected, (state) => {
        state.coursesStatus = 'rejected'
      })
      .addCase(
        fetchCourses.fulfilled,
        (state, action: PayloadAction<Course[]>) => {
          state.coursesStatus = 'fulfilled'
          state.courses = action.payload
        },
      )
      .addCase(fetchInvites.pending, (state) => {
        state.invitesStatus = 'pending'
      })
      .addCase(fetchInvites.rejected, (state) => {
        state.invitesStatus = 'rejected'
      })
      .addCase(
        fetchInvites.fulfilled,
        (state, action: PayloadAction<UserInvite[]>) => {
          state.invitesStatus = 'fulfilled'
          state.invites = action.payload
        },
      )
      .addCase(fetchCertificates.pending, (state) => {
        state.certificatesStatus = 'pending'
      })
      .addCase(fetchCertificates.rejected, (state) => {
        state.certificatesStatus = 'rejected'
      })
      .addCase(
        fetchCertificates.fulfilled,
        (state, action: PayloadAction<RecordedCertificate[]>) => {
          state.certificatesStatus = 'fulfilled'
          state.certificates = action.payload
        },
      )
      .addCase(fetchLiveCertificates.pending, (state) => {
        state.liveCertificatesStatus = 'pending'
      })
      .addCase(fetchLiveCertificates.rejected, (state) => {
        state.liveCertificatesStatus = 'rejected'
      })
      .addCase(
        fetchLiveCertificates.fulfilled,
        (state, action: PayloadAction<LiveCertificate[]>) => {
          state.liveCertificatesStatus = 'fulfilled'
          state.liveCertificates = action.payload
        },
      )
      .addCase(fetchExternalCertificates.pending, (state) => {
        state.externalCertificatesStatus = 'pending'
      })
      .addCase(fetchExternalCertificates.rejected, (state) => {
        state.externalCertificatesStatus = 'rejected'
      })
      .addCase(
        fetchExternalCertificates.fulfilled,
        (state, action: PayloadAction<ExternalCertificate[]>) => {
          state.externalCertificatesStatus = 'fulfilled'
          state.externalCertificates = action.payload
        },
      )
      .addCase(fetchCredentials.pending, (state) => {
        state.credentialsStatus = 'pending'
      })
      .addCase(fetchCredentials.rejected, (state) => {
        state.credentialsStatus = 'rejected'
      })
      .addCase(
        fetchCredentials.fulfilled,
        (state, action: PayloadAction<Credential[]>) => {
          state.credentialsStatus = 'fulfilled'
          state.credentials = action.payload
        },
      )
      .addCase(fetchQuizAnalytics.pending, (state) => {
        state.quizAnalyticsStatus = 'pending'
      })
      .addCase(fetchQuizAnalytics.rejected, (state) => {
        state.quizAnalyticsStatus = 'rejected'
      })
      .addCase(
        fetchQuizAnalytics.fulfilled,
        (state, action: PayloadAction<QuizData>) => {
          state.quizAnalyticsStatus = 'fulfilled'
          state.quizAnalytics[state.activeLinkedService] = action.payload
        },
      )
      .addCase(fetchLinkedServices.pending, (state) => {
        state.linkedServicesStatus = 'pending'
      })
      .addCase(fetchLinkedServices.rejected, (state) => {
        state.linkedServicesStatus = 'rejected'
      })
      .addCase(fetchLinkedServices.fulfilled, (state, action) => {
        state.linkedServicesStatus = 'fulfilled'
        state.linkedServices = action.payload
      })
      .addCase(fetchLicenses.pending, (state, action) => {
        state.licensesStatus[action.meta.arg] = 'pending'
      })
      .addCase(fetchLicenses.rejected, (state, action) => {
        state.licensesStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchLicenses.fulfilled, (state, action) => {
        state.licensesStatus[action.meta.arg] = 'fulfilled'
        state.licenses[action.meta.arg] = action.payload
      })
  },
})

export let {
  activeLinkedServiceChanged,
  groupsChanged,
  usersChanged,
  assignmentsChanged,
  trainingSeriesChanged,
  invitesChanged,
  certificatesChanged,
  credentialsChanged,
  successMessageChanged,
  errorMessageChanged,
} = manageSlice.actions

export let selectActiveLinkedService = (state: AppRootState) =>
  state.manage.activeLinkedService

export let selectUserHoursByLinkedService =
  (id: string) => (state: AppRootState) =>
    state.manage.userHours.find((uh) => uh.linkedServiceID === id)
export let selectUserHoursStatus = (state: AppRootState) =>
  state.manage.userHoursStatus
export let selectQuizAnalytics = (state: AppRootState) =>
  state.manage.quizAnalytics

export let selectGroups = (state: AppRootState) =>
  [...state.manage.groups].sort((a, b) => a.name.localeCompare(b.name))
export let selectGroupsStatus = (state: AppRootState) =>
  state.manage.groupsStatus
export let selectGroupById = (id: string) => (state: AppRootState) =>
  state.manage.groups.find((g) => g.id === id)
export let selectGroupsByLinkedService =
  (id: string) => (state: AppRootState) =>
    state.manage.groups
      .filter((g) => g.linkedServiceID === id)
      .sort((a, b) => a.name.localeCompare(b.name))

export let selectUsers = (state: AppRootState) => state.manage.users
export let selectUsersStatus = (state: AppRootState) => state.manage.usersStatus
export let selectUsersSortedWithName = (state: AppRootState) =>
  state.manage.users
    .map((user) => ({
      ...user,
      ...(user.lastName === 'Unknown'
        ? {
            name: user.primaryEmail,
            firstName: user.primaryEmail,
            lastName: user.primaryEmail,
          }
        : {
            name: `${user.firstName} ${user.lastName}`,
          }),
    }))
    .sort((a, b) => a.lastName.localeCompare(b.lastName))
export let selectUserById = (id: string) => (state: AppRootState) =>
  selectUsersSortedWithName(state).find((u) => u.id === id)
export let selectUserByEmail = (email: string) => (state: AppRootState) =>
  selectUsersSortedWithName(state).find((u) => u.primaryEmail === email)
export let selectUsersByGroupId = (id: string) => (state: AppRootState) =>
  state.manage.users
    .filter((u) => selectGroupById(id)(state)?.users?.some((gu) => gu === u.id))
    .map((user) => ({
      ...user,
      name: `${user.firstName} ${user.lastName}`,
    }))
    .sort((a, b) => a.lastName.localeCompare(b.lastName))
export let selectUsersByLinkedService = (id: string) => (state: AppRootState) =>
  selectUsersSortedWithName(state).filter((u) =>
    u.linkedServiceIDs?.includes(id),
  )
export let selectAlphabetizedUsersByLinkedService =
  (id: string) => (state: AppRootState) =>
    selectUsersByLinkedService(id)(state).sort((a, b) =>
      a.lastName.localeCompare(b.lastName),
    )
export let selectUsersByNames = (names: string[]) => (state: AppRootState) =>
  state.manage.users.filter((u) =>
    names.includes(`${u.firstName} ${u.lastName}`),
  )

export let selectAssignments = (state: AppRootState) =>
  state.manage.assignments
    .filter((a) => a.name)
    .sort((a, b) => a.name.localeCompare(b.name))
export let selectAssignmentsStatus = (state: AppRootState) =>
  state.manage.assignmentsStatus
export let selectAssignmentById = (id: string) => (state: AppRootState) =>
  state.manage.assignments.find((a) => a.id === id)
export let selectAssignmentsByLinkedService =
  (id: string) => (state: AppRootState) =>
    state.manage.assignments
      .filter((a) => a.name && a.linkedServiceID === id)
      .sort((a, b) => a.name.localeCompare(b.name))
export let selectUsersByAssignmentId = (id: string) => (state: AppRootState) =>
  state.manage.users
    .filter((u) =>
      selectAssignmentById(id)(state)?.users.some((au) => au === u.id),
    )
    .map((user) => ({
      ...user,
      name: `${user.firstName} ${user.lastName}`,
    }))
    .sort((a, b) => a.lastName.localeCompare(b.lastName))
export let selectCompletedAssignmentsWithinThreshold =
  (id: string) => (state: AppRootState) =>
    state.manage.completedAssignmentsWithinThreshold[id]
export let selectCompletedAssignmentsWithinThresholdStatus =
  (id: string) => (state: AppRootState) =>
    state.manage.completedAssignmentsWithinThresholdStatus[id]
export let selectCompletedAssignmentsByLinkedService =
  (id: string) => (state: AppRootState) =>
    state.manage.completedAssignments
      .filter((a) => a.linkedServiceID === id)
      .sort((a, b) => dayjs(b.dateCompleted).diff(a.dateCompleted))
export let selectCompletedAssignmentsStatus = (state: AppRootState) =>
  state.manage.completedAssignmentsStatus

export let selectSections = (state: AppRootState) => state.manage.sections
export let selectSectionsStatus = (state: AppRootState) =>
  state.manage.sectinsStatus

export let selectCompletedCourses = (state: AppRootState) =>
  state.manage.completedCourses
export let selectCompletedCoursesByAssignment =
  (assignmentID: string) => (state: AppRootState) =>
    state.manage.completedCourses.filter(
      (cc) => cc.assignmentID === assignmentID,
    )
export let selectCompletedCoursesStatus = (state: AppRootState) =>
  state.manage.completedCoursesStatus

export let selectTrainingSeries = (state: AppRootState) =>
  state.manage.trainingSeries
export let selectTrainingSeriesStatus = (state: AppRootState) =>
  state.manage.trainingSeriesStatus
export let selectTrainingSeriesById =
  (trainingSeriesId?: string) => (state: AppRootState) =>
    state.manage.trainingSeries.find((ts) => ts.id === trainingSeriesId)
export let selectTrainingSeriesByLinkedService =
  (id: string) => (state: AppRootState) =>
    state.manage.trainingSeries
      .filter((ts) => ts.linkedServiceID === id)
      .sort((a, b) => a.name.localeCompare(b.name))

export let selectClasses = (state: AppRootState) => state.manage.classes
export let selectClassesStatus = (state: AppRootState) =>
  state.manage.classesStatus

export let selectCourses = (state: AppRootState) => state.manage.courses
export let selectCoursesStatus = (state: AppRootState) =>
  state.manage.coursesStatus
export let selectCoursesByAssignmentByID =
  (id: string) => (state: AppRootState) =>
    state.manage.courses
      .filter((c) =>
        selectAssignmentById(id)(state)?.courses.some((ac) => ac === c.id),
      )
      .sort((a, b) => a.name.localeCompare(b.name))
export let selectCoursesByTrainingSeriesID =
  (id: string) => (state: AppRootState) =>
    state.manage.courses
      .filter((c) =>
        selectTrainingSeriesById(id)(state)?.courseIDs.some(
          (tsc) => tsc === c.id,
        ),
      )
      .sort((a, b) => a.name.localeCompare(b.name))

export let selectInvites = (state: AppRootState) => state.manage.invites
export let selectInvitesStatus = (state: AppRootState) =>
  state.manage.invitesStatus
export let selectInvitesByLinkedService =
  (id: string) => (state: AppRootState) =>
    state.manage.invites
      .filter((a) => a.linkedServiceID === id)
      .sort((a, b) => a.userEmail.localeCompare(b.userEmail))

export let selectCertificates = (state: AppRootState) =>
  state.manage.certificates
export let selectCertificatesStatus = (state: AppRootState) =>
  state.manage.certificatesStatus
export let selectCertificatesByUserID = (id: string) => (state: AppRootState) =>
  state.manage.certificates.filter((c) => c.userID === id)

export let selectLiveCertificatesByUserID =
  (id: string) => (state: AppRootState) =>
    state.manage.liveCertificates.filter((c) => c.userID === id)
export let selectLiveCertificatesStatus = (state: AppRootState) =>
  state.manage.liveCertificatesStatus

export let selectExternalCertificatesByUserID =
  (id: string) => (state: AppRootState) =>
    state.manage.externalCertificates.filter((c) => c.userID === id)
export let selectExternalCertificatesStatus = (state: AppRootState) =>
  state.manage.externalCertificatesStatus

export let selectCredentials = (state: AppRootState) =>
  state.manage.credentials.map((c) => ({
    ...c,
    credentialType: typeof c.type === 'string' ? c.type : c.type.other,
  }))
export let selectCredentialsStatus = (state: AppRootState) =>
  state.manage.credentialsStatus
export let selectCredentialsByUserId = (id: string) => (state: AppRootState) =>
  selectCredentials(state)
    .filter((c) => c.userID === id)
    .sort((a, b) => dayjs(b.createdTime).diff(a.createdTime))

export let selectLinkedServices = (state: AppRootState) =>
  state.manage.linkedServices
export let selectLinkedServiceById = (id: string) => (state: AppRootState) =>
  state.manage.linkedServices.find((x) => x.id === id)
export let selectLinkedServicesByIds =
  (ids: string[]) => (state: AppRootState) =>
    state.manage.linkedServices.filter((x) => ids.includes(x.id ?? ''))
export let selectLinkedServicesStatus = (state: AppRootState) =>
  state.manage.linkedServicesStatus

export let selectSuccessMessage = (state: AppRootState) =>
  state.manage.successMessage

export let selectErrorMessage = (state: AppRootState) =>
  state.manage.errorMessage

export let selectLicenses = (state: AppRootState) => state.manage.licenses
export let selectLicensesStatus = (state: AppRootState) =>
  state.manage.licensesStatus

export let selectGroupsWithDefaults =
  (id: string) =>
  (state: AppRootState): UserGroup[] => [
    ...['EMT', 'AEMT', 'Paramedic', 'Registered Nurse'].map((licenseType) => ({
      id: licenseType,
      name: `${licenseType} (auto)`,
      linkedServiceID: 'FOAMfrat',
      users: selectUsersByLinkedService(id)(state)
        .filter(
          (u) =>
            u.licenseType?.toLowerCase() === licenseType.toLowerCase() ||
            selectLicenses(state)[selectActiveLinkedService(state)]?.some(
              (l) =>
                l.userID === u.id &&
                l.type.toLowerCase() === licenseType.toLowerCase(),
            ),
        )
        .map((u) => u.id),
    })),
    ...selectGroupsByLinkedService(id)(state),
  ]

export default manageSlice.reducer
